// ============================================================================
// languageConfig — catálogo DINÂMICO de idiomas e pares de tradução, gerido
// pelo master em /company/master/languages e guardado no doc `global/languages`:
//   { languages: [...], pairs: [...] }
//
// Por que existe: até aqui cada idioma novo obrigava a mexer em ~15 arquivos
// (selects, if/else de preço, áudio, moeda, certificado, Tradume, docs). Agora
// o idioma é DADO, não código.
//
// Duas decisões que sustentam o desenho:
//
//  1) IDENTIDADE = o rótulo em português ("Português (Brasil)", "Francês").
//     É o que já está gravado em milhares de projetos e no contrato da API
//     externa. O `id` (pt-BR, en, fr…) serve para ligar os pares entre si;
//     `aliases` absorve as variações históricas ("Português" sem o sufixo, "PT",
//     "English"), de modo que nada gravado no passado deixa de casar.
//
//  2) CHAVE DE TARIFA = campo do par (`rateKey`), não derivada do idioma.
//     Os pares atuais apontam para os campos legados (pttoen, frtoen, ittoen…)
//     que já vivem em users/{uid}, priceperpage/price e global/rates — logo,
//     ZERO migração de dados. Par novo ganha uma chave gerada (rate_de_en).
// ============================================================================
import { doc, getDoc, setDoc } from "firebase/firestore";
import { safeOnSnapshot } from "./safeSnapshot";
import { db } from "../firebaseConfig";

export const LANGUAGE_DOC = ["global", "languages"];

// Sufixo do campo de tarifa de áudio (por minuto) derivado do campo por página.
export const AUDIO_SUFFIX = "_audio";

// Catálogo padrão — espelha exatamente o que existia hardcoded antes desta lib.
// Serve de semente da página do master e de fallback quando o doc não existe.
export const DEFAULT_LANGUAGES = [
  {
    id: "pt-BR",
    label: "Português (Brasil)",
    aliases: ["Português", "Portugues", "Português (BR)", "Portugues (BR)", "PT", "PT-BR", "Portuguese"],
    i18nKey: "forms.portugueseBrazil",
    short: "Por",
    certName: "Portuguese (Brazil)",
    currency: "BRL",
    tradumeSource: "PT",
    tradumeTarget: "PT-BR",
    active: true,
  },
  {
    id: "en",
    label: "Inglês",
    aliases: ["Ingles", "English", "EN"],
    i18nKey: "forms.english",
    short: "Ing",
    certName: "English",
    currency: "USD",
    tradumeSource: "EN",
    tradumeTarget: "EN-US",
    active: true,
  },
  {
    id: "es-419",
    label: "Espanhol (América Latina)",
    aliases: ["Espanhol", "Espanhol (LATAM)", "Español", "Spanish", "ES"],
    i18nKey: "forms.spanishLatinAmerica",
    short: "Esp",
    certName: "Spanish (Latin America)",
    currency: "EUR",
    tradumeSource: "ES",
    tradumeTarget: "ES",
    active: true,
  },
  {
    id: "fr",
    label: "Francês",
    aliases: ["Frances", "French", "FR"],
    i18nKey: "forms.french",
    short: "Fr",
    certName: "French",
    currency: "EUR",
    tradumeSource: "FR",
    tradumeTarget: "FR",
    active: true,
  },
  {
    id: "it",
    label: "Italiano",
    aliases: ["Italian", "IT"],
    i18nKey: "forms.italian",
    short: "Ita",
    certName: "Italian",
    currency: "EUR",
    tradumeSource: "IT",
    tradumeTarget: "IT",
    active: true,
  },
];

// Pares ativos. `rateKey` = campo de tarifa por página; o de áudio é
// rateKey + "_audio" salvo quando `audioKey` vier explícito.
export const DEFAULT_PAIRS = [
  { from: "pt-BR", to: "en", rateKey: "pttoen", active: true },
  { from: "es-419", to: "en", rateKey: "esptoen", active: true },
  { from: "fr", to: "en", rateKey: "frtoen", active: true },
  { from: "it", to: "en", rateKey: "ittoen", active: true },
  { from: "en", to: "pt-BR", rateKey: "entopt", active: true },
  { from: "en", to: "es-419", rateKey: "entoesp", active: true },
];

// Pares de tradução JURAMENTADA NO BRASIL — lista SEPARADA dos pares acima
// (que são os da tradução certificada nos EUA / não certificada). Mesmo
// formato { from, to, rateKey, active }, mas a tarifa é própria e todo par
// precisa ter Português em um dos lados (isPortugueseLanguage). Começa vazia:
// o master cadastra em Empresa → Idiomas.
export const DEFAULT_SWORN_PAIRS = [];

export const DEFAULT_LANGUAGE_CONFIG = {
  languages: DEFAULT_LANGUAGES,
  pairs: DEFAULT_PAIRS,
  swornPairs: DEFAULT_SWORN_PAIRS,
};

// Normalização usada em TODA comparação de idioma: sem acento, minúsculo, sem
// pontuação, espaços colapsados. "Português (Brasil)" → "portugues brasil".
export const normalizeLanguageText = (text) => {
  if (!text) return "";
  return String(text)
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

// Regra da juramentada: Português em um dos lados. Por id ("pt-BR", "pt-PT"…)
// ou pelo rótulo ("Português (Portugal)"), para valer também para uma variante
// de Português que o master venha a cadastrar.
export function isPortugueseLanguage(lang) {
  if (!lang) return false;
  const id = String(lang.id || "").toLowerCase();
  return id === "pt" || id.startsWith("pt-") || normalizeLanguageText(lang.label).startsWith("portugues");
}

// Sanitiza o que veio do Firestore (o master pode ter salvo lista incompleta).
const normalizeConfig = (data) => {
  const langs = Array.isArray(data?.languages) ? data.languages : [];
  const pairs = Array.isArray(data?.pairs) ? data.pairs : [];

  const languages = langs
    .map((l) => ({
      id: String(l?.id || "").trim(),
      label: String(l?.label || "").trim(),
      aliases: Array.isArray(l?.aliases) ? l.aliases.filter(Boolean).map(String) : [],
      i18nKey: l?.i18nKey || "",
      short: String(l?.short || "").trim(),
      certName: String(l?.certName || "").trim(),
      currency: String(l?.currency || "").toUpperCase().trim(),
      tradumeSource: String(l?.tradumeSource || "").toUpperCase().trim(),
      tradumeTarget: String(l?.tradumeTarget || "").toUpperCase().trim(),
      active: l?.active !== false,
    }))
    .filter((l) => l.id && l.label);

  const ids = new Set(languages.map((l) => l.id));
  const cleanPairs = pairs
    .map((p) => ({
      from: String(p?.from || "").trim(),
      to: String(p?.to || "").trim(),
      rateKey: String(p?.rateKey || "").trim(),
      audioKey: String(p?.audioKey || "").trim(),
      active: p?.active !== false,
    }))
    // Par que aponta para idioma inexistente é lixo — descarta.
    .filter((p) => p.from && p.to && p.rateKey && ids.has(p.from) && ids.has(p.to));

  if (!languages.length || !cleanPairs.length) return { ...DEFAULT_LANGUAGE_CONFIG };

  // Juramentada: mesmo saneamento + a regra do Português. Par sem Português
  // é descartado aqui também, não só na tela (vale para gravação direta no banco).
  const byId = Object.fromEntries(languages.map((l) => [l.id, l]));
  const swornPairs = (Array.isArray(data?.swornPairs) ? data.swornPairs : [])
    .map((p) => ({
      from: String(p?.from || "").trim(),
      to: String(p?.to || "").trim(),
      rateKey: String(p?.rateKey || "").trim(),
      active: p?.active !== false,
    }))
    .filter((p) => p.from && p.to && p.from !== p.to && p.rateKey && byId[p.from] && byId[p.to])
    .filter((p) => isPortugueseLanguage(byId[p.from]) || isPortugueseLanguage(byId[p.to]));

  return { languages, pairs: cleanPairs, swornPairs };
};

// --- Consultas ------------------------------------------------------------

const cfgOf = (config) => (config && config.languages?.length ? config : DEFAULT_LANGUAGE_CONFIG);

// Resolve QUALQUER forma de escrever um idioma (id, rótulo atual, rótulo
// antigo, alias, com ou sem acento) para o idioma do catálogo. É o que evita
// que "Português" gravado por uma tela antiga deixe de casar com a tarifa.
export function resolveLanguage(value, config) {
  const { languages } = cfgOf(config);
  if (!value) return null;
  const raw = String(value).trim();
  const norm = normalizeLanguageText(raw);
  if (!norm) return null;

  const byId = languages.find((l) => l.id.toLowerCase() === raw.toLowerCase());
  if (byId) return byId;

  const byLabel = languages.find((l) => normalizeLanguageText(l.label) === norm);
  if (byLabel) return byLabel;

  const byAlias = languages.find((l) =>
    (l.aliases || []).some((a) => normalizeLanguageText(a) === norm)
  );
  if (byAlias) return byAlias;

  // Último recurso: prefixo ("espanhol" casa "espanhol america latina"). Só
  // vale se UM idioma casar — senão seria adivinhação.
  const byPrefix = languages.filter(
    (l) => normalizeLanguageText(l.label).startsWith(norm) || norm.startsWith(normalizeLanguageText(l.label))
  );
  return byPrefix.length === 1 ? byPrefix[0] : null;
}

// Rótulo canônico (o valor que deve ser GRAVADO no projeto).
export function canonicalLabel(value, config) {
  const lang = resolveLanguage(value, config);
  return lang ? lang.label : String(value || "");
}

// Nome para EXIBIÇÃO, traduzido quando o idioma tem chave i18n (os 5 originais);
// senão o rótulo cadastrado pelo master. `t` é o do react-i18next (opcional).
export function languageDisplayName(lang, t) {
  if (!lang) return "";
  if (t && lang.i18nKey) {
    const traduzido = t(lang.i18nKey);
    // t() devolve a própria chave quando não existe tradução.
    if (traduzido && traduzido !== lang.i18nKey) return traduzido;
  }
  return lang.label;
}

export const activeLanguages = (config) => cfgOf(config).languages.filter((l) => l.active !== false);
export const activePairs = (config) => cfgOf(config).pairs.filter((p) => p.active !== false);

export function languageById(id, config) {
  return cfgOf(config).languages.find((l) => l.id === id) || null;
}

// Par de tradução (origem→destino) ou null quando a combinação não é ofertada.
export function findPair(source, target, config) {
  const cfg = cfgOf(config);
  const s = resolveLanguage(source, cfg);
  const tgt = resolveLanguage(target, cfg);
  if (!s || !tgt) return null;
  return activePairs(cfg).find((p) => p.from === s.id && p.to === tgt.id) || null;
}

// Campo da tarifa POR PÁGINA do par (ex.: "pttoen") ou null.
export function rateKeyFor(source, target, config) {
  const pair = findPair(source, target, config);
  return pair ? pair.rateKey : null;
}

// Campo da tarifa de ÁUDIO (por minuto) do par (ex.: "pttoen_audio") ou null.
export function audioKeyFor(source, target, config) {
  const pair = findPair(source, target, config);
  if (!pair) return null;
  return pair.audioKey || `${pair.rateKey}${AUDIO_SUFFIX}`;
}

// Valor por página do par, a partir de um objeto de tarifas do cliente.
export function ratePerPage(source, target, rates, config) {
  const key = rateKeyFor(source, target, config);
  if (!key || !rates) return 0;
  return Number(rates[key]) || 0;
}

// Todos os campos de tarifa (página e áudio) dos pares ativos — usado para
// montar objetos de tarifa, formulários e a cópia de valores no cadastro.
export function allRateKeys(config) {
  return activePairs(config).map((p) => p.rateKey);
}
export function allAudioKeys(config) {
  return activePairs(config).map((p) => p.audioKey || `${p.rateKey}${AUDIO_SUFFIX}`);
}

// Idiomas que podem ser ORIGEM (têm ao menos um par ativo saindo deles).
export function sourceLanguages(config) {
  const cfg = cfgOf(config);
  const ids = new Set(activePairs(cfg).map((p) => p.from));
  return activeLanguages(cfg).filter((l) => ids.has(l.id));
}

// Destinos possíveis para uma origem. Vazio = origem sem par cadastrado.
export function targetLanguagesFor(source, config) {
  const cfg = cfgOf(config);
  const s = resolveLanguage(source, cfg);
  if (!s) return [];
  const ids = activePairs(cfg)
    .filter((p) => p.from === s.id)
    .map((p) => p.to);
  return activeLanguages(cfg).filter((l) => ids.includes(l.id));
}

// Sigla curta do par para cabeçalhos de tabela ("Por/Ing", "Ita/Ing").
export function pairShortLabel(pair, config) {
  const cfg = cfgOf(config);
  const from = languageById(pair.from, cfg);
  const to = languageById(pair.to, cfg);
  const abbr = (l, fallback) => (l?.short || l?.label?.slice(0, 3) || fallback || "?");
  return `${abbr(from, pair.from)}/${abbr(to, pair.to)}`;
}

// Rótulo longo do par ("Português (Brasil) → Inglês").
export function pairLabel(pair, config, t) {
  const cfg = cfgOf(config);
  const from = languageById(pair.from, cfg);
  const to = languageById(pair.to, cfg);
  return `${languageDisplayName(from, t) || pair.from} → ${languageDisplayName(to, t) || pair.to}`;
}

// --- Juramentada no Brasil ------------------------------------------------

export const activeSwornPairs = (config) =>
  (cfgOf(config).swornPairs || []).filter((p) => p.active !== false);

// Par juramentado (origem→destino) ou null quando não é ofertado.
export function findSwornPair(source, target, config) {
  const cfg = cfgOf(config);
  const s = resolveLanguage(source, cfg);
  const tgt = resolveLanguage(target, cfg);
  if (!s || !tgt) return null;
  return activeSwornPairs(cfg).find((p) => p.from === s.id && p.to === tgt.id) || null;
}

// A combinação aceita Juramentada no Brasil?
export const swornAvailable = (source, target, config) => !!findSwornPair(source, target, config);

// Campo da tarifa juramentada por página do par (ex.: "sworn_pt_br_to_en") ou null.
export function swornRateKeyFor(source, target, config) {
  const pair = findSwornPair(source, target, config);
  return pair ? pair.rateKey : null;
}

// Todos os campos de tarifa juramentada dos pares ativos.
export function allSwornRateKeys(config) {
  return activeSwornPairs(config).map((p) => p.rateKey);
}

// --- Tipo de tradução (certificationType) ---------------------------------
// "usa"      → Certificada nos EUA (pares normais, tarifa normal)
// "none"     → Não certificada     (pares normais, tarifa normal)
// "sworn_br" → Juramentada no Brasil (pares juramentados, tarifa própria)
export const CERTIFICATION_TYPES = ["usa", "sworn_br", "none"];
export const DEFAULT_CERTIFICATION_TYPE = "usa";

// Tipo do projeto, inclusive dos antigos (só tinham isCertified).
export function projectCertificationType(project) {
  if (CERTIFICATION_TYPES.includes(project?.certificationType)) return project.certificationType;
  return project?.isCertified ? "usa" : "none";
}

const isSworn = (type) => type === "sworn_br";

// Lista de pares que vale para o tipo.
const pairsForType = (type, config) => (isSworn(type) ? activeSwornPairs(config) : activePairs(config));

export function sourceLanguagesForType(type, config) {
  const cfg = cfgOf(config);
  const ids = new Set(pairsForType(type, cfg).map((p) => p.from));
  return activeLanguages(cfg).filter((l) => ids.has(l.id));
}

export function targetLanguagesForType(source, type, config) {
  const cfg = cfgOf(config);
  const s = resolveLanguage(source, cfg);
  if (!s) return [];
  const ids = pairsForType(type, cfg).filter((p) => p.from === s.id).map((p) => p.to);
  return activeLanguages(cfg).filter((l) => ids.includes(l.id));
}

// A combinação origem→destino existe para o tipo?
export function pairAvailableForType(source, target, type, config) {
  return isSworn(type) ? !!findSwornPair(source, target, config) : !!findPair(source, target, config);
}

// Campo de tarifa por página conforme o tipo (juramentada usa a sua).
export function rateKeyForType(source, target, type, config) {
  return isSworn(type) ? swornRateKeyFor(source, target, config) : rateKeyFor(source, target, config);
}

export function ratePerPageForType(source, target, type, rates, config) {
  const key = rateKeyForType(source, target, type, config);
  if (!key || !rates) return 0;
  return Number(rates[key]) || 0;
}

// Moeda sugerida pelo idioma (usada para pré-selecionar o par da conversão).
export function currencyForLanguage(value, config) {
  const lang = resolveLanguage(value, config);
  return lang?.currency || null;
}

// Nome do idioma em inglês para a capa do certificado.
export function certificateLanguageName(value, config) {
  const lang = resolveLanguage(value, config);
  return lang?.certName || String(value || "N/A");
}

// Código do idioma aceito pelo Tradume/DeepL (target exige variante: PT-BR/EN-US).
export function tradumeCode(value, isTarget, config) {
  const lang = resolveLanguage(value, config);
  if (!lang) return null;
  return (isTarget ? lang.tradumeTarget : lang.tradumeSource) || null;
}

// --- Persistência ---------------------------------------------------------

export async function loadLanguageConfig() {
  try {
    const snap = await getDoc(doc(db, ...LANGUAGE_DOC));
    if (snap.exists()) return normalizeConfig(snap.data());
  } catch (e) {
    console.warn("Erro ao carregar idiomas:", e?.message);
  }
  return { ...DEFAULT_LANGUAGE_CONFIG };
}

export function subscribeLanguageConfig(cb) {
  return safeOnSnapshot(
    doc(db, ...LANGUAGE_DOC),
    (snap) => cb(snap.exists() ? normalizeConfig(snap.data()) : { ...DEFAULT_LANGUAGE_CONFIG }),
    {
      label: "languages-doc",
      onError: (e) => {
        console.warn("Erro ao assinar idiomas:", e?.message);
        cb({ ...DEFAULT_LANGUAGE_CONFIG });
      },
    }
  );
}

export async function saveLanguageConfig({ languages, pairs, swornPairs }) {
  const payload = normalizeConfig({ languages, pairs, swornPairs });
  await setDoc(doc(db, ...LANGUAGE_DOC), payload, { merge: false });
  return payload;
}
